import { useState, useCallback, useMemo } from 'react';
import { useOutletContext, useSearchParams, useNavigate } from 'react-router-dom';
import {
    useLedgerHistoricalValues,
    useLedgerHistoricalPnl,
    useLedgerHistoricalMwrr,
} from '@/api/hooks/useLedgerAnalytics';
import type { AnalyticsGranularity, AnalyticsMode, HistoricalValueItem } from '@/api/types';
import type { LedgerDetailOutletContext } from '../LedgerDetailPage';
import { ModeToggle, ToggleOption } from './ModeToggle';
import { GranularityToggle } from './GranularityToggle';
import { HistoricalValueChart } from './HistoricalValueChart';
import { HistoricalValueTable } from './HistoricalValueTable';
import { ProfitLossTable } from './ProfitLossTable';
import { ReturnsTable } from './ReturnsTable';
import { PnlChartTooltip } from './PnlChartTooltip';
import { ReturnsChartTooltip } from './ReturnsChartTooltip';
import { StatsCards } from './StatsCards';
import { useChartDimensions } from './hooks/useChartDimensions';
import { ChartTableToggle } from '@/components/ui/ChartTableToggle';
import { DownloadMenu } from '@/components/ui/DownloadMenu';
import { Spinner } from '@/components/ui/Spinner';
import { KnowledgeLink } from '@/components/ui/KnowledgeLink';
import { formatDateISO } from '@/utils/date';
import type { ColumnDefinition } from '@/lib/export';

// localStorage keys for fallback defaults
const STORAGE_KEY_GRANULARITY = 'granularity-ledger';
const STORAGE_KEY_VIEW_MODE = 'view-mode-ledger';

// Helper to get granularity from URL with localStorage fallback
function getGranularityFromParams(searchParams: URLSearchParams): AnalyticsGranularity {
    const urlValue = searchParams.get('granularity');
    if (urlValue === 'month' || urlValue === 'year') return urlValue;
    try {
        const stored = localStorage.getItem(STORAGE_KEY_GRANULARITY);
        if (stored === 'month' || stored === 'year') return stored;
    } catch {
        // localStorage unavailable
    }
    return 'month';
}

// Helper to get viewMode from URL with localStorage fallback
function getViewModeFromParams(searchParams: URLSearchParams): 'chart' | 'table' {
    const urlValue = searchParams.get('view');
    if (urlValue === 'chart' || urlValue === 'table') return urlValue;
    try {
        const stored = localStorage.getItem(STORAGE_KEY_VIEW_MODE);
        if (stored === 'chart' || stored === 'table') return stored;
    } catch {
        // localStorage unavailable
    }
    return 'chart';
}

interface BrushRange {
    startIndex: number;
    endIndex: number;
}

/**
 * Sanitize ledger name for use in filename.
 * Converts to lowercase, replaces spaces with hyphens, removes special characters.
 */
function sanitizeFilename(name: string): string {
    return name
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
}

/**
 * Column definitions for value mode export.
 */
const VALUE_EXPORT_COLUMNS: ColumnDefinition[] = [
    { key: 'periodLabel', label: 'Period', format: 'date' },
    { key: 'value', label: 'Value', format: 'money' },
];

/**
 * Column definitions for P&L mode export.
 */
const PNL_EXPORT_COLUMNS: ColumnDefinition[] = [
    { key: 'periodLabel', label: 'Period', format: 'date' },
    { key: 'netProfit', label: 'Net Profit', format: 'money' },
    { key: 'gainPercentage', label: 'Gain %', format: 'percent' },
    { key: 'changeFromPrevious', label: 'Change', format: 'money' },
];

/**
 * Column definitions for MWRR mode export.
 */
const MWRR_EXPORT_COLUMNS: ColumnDefinition[] = [
    { key: 'periodLabel', label: 'Period', format: 'date' },
    { key: 'mwrr', label: 'MWRR %', format: 'percent' },
];


/**
 * Analytics tab component for displaying ledger analytics.
 * Features:
 * - Stats cards showing P&L, MWRR, and cashflow totals (assets only)
 * - Mode toggle (Value/P&L/MWRR) - P&L/MWRR hidden for debt ledgers
 * - Granularity toggle (M/Y) - triggers data refetch
 * - Chart/Table toggle for switching between visualization modes
 * - Download menu for CSV/JSON export
 * - Historical value chart with brush for date range selection
 * - P&L chart when mode='pnl' with custom tooltip
 * - MWRR chart with percentage Y-axis and simple tooltip
 * - Loading overlay during data fetch (smooth transitions)
 * - Responsive chart heights
 */
export function AnalyticsTab() {
    // Get ledger from parent context
    const { ledger } = useOutletContext<LedgerDetailOutletContext>();

    // Null safety: if ledger is not available, return null and let parent handle 404
    if (!ledger) return null;

    const navigate = useNavigate();
    const isDebt = ledger.type === 'debt';
    const isAsset = ledger.type === 'asset';
    const typeLabel = isDebt ? 'debt' : 'asset';

    // URL-based metric tracking
    const [searchParams, setSearchParams] = useSearchParams();
    const metricParam = searchParams.get('metric');

    // Validate metric param: must be 'value', or for assets also 'pnl' or 'mwrr'
    const isValidMetric = (param: string | null): param is AnalyticsMode => {
        if (!param) return false;
        if (param === 'value') return true;
        if (isAsset && (param === 'pnl' || param === 'mwrr')) return true;
        return false;
    };

    const mode: AnalyticsMode = isValidMetric(metricParam) ? metricParam : 'value';

    // URL-based granularity with localStorage fallback
    const granularity = getGranularityFromParams(searchParams);

    // URL-based viewMode with localStorage fallback
    const viewMode = getViewModeFromParams(searchParams);

    // Helper to update URL params while preserving others
    const updateUrlParams = useCallback((updates: Record<string, string>) => {
        const params = new URLSearchParams(searchParams);
        Object.entries(updates).forEach(([key, value]) => {
            params.set(key, value);
        });
        setSearchParams(params, { replace: true });
    }, [searchParams, setSearchParams]);

    // Setters that update URL and persist to localStorage
    const setMode = useCallback((newMode: AnalyticsMode) => {
        updateUrlParams({ metric: newMode });
    }, [updateUrlParams]);

    const setGranularity = useCallback((newGranularity: AnalyticsGranularity) => {
        try {
            localStorage.setItem(STORAGE_KEY_GRANULARITY, newGranularity);
        } catch {
            // localStorage unavailable
        }
        updateUrlParams({ granularity: newGranularity });
    }, [updateUrlParams]);

    const setViewMode = useCallback((newViewMode: 'chart' | 'table') => {
        try {
            localStorage.setItem(STORAGE_KEY_VIEW_MODE, newViewMode);
        } catch {
            // localStorage unavailable
        }
        updateUrlParams({ view: newViewMode });
    }, [updateUrlParams]);

    // Local UI state (brush doesn't need URL persistence)
    const [brushRange, setBrushRange] = useState<BrushRange | null>(null);

    // Determine if current mode is a returns mode (mwrr)
    const isReturnsMode = mode === 'mwrr';

    // Container dimensions for responsive chart
    const { containerRef, dimensions } = useChartDimensions();

    // Fetch historical values (always needed for value mode)
    const valueQuery = useLedgerHistoricalValues({
        ledgerId: ledger.id,
        granularity,
    });

    // Fetch historical P&L (only when in pnl mode and not a debt ledger)
    const pnlQuery = useLedgerHistoricalPnl({
        ledgerId: mode === 'pnl' && !isDebt ? ledger.id : 0,
        granularity,
    });

    // Fetch historical MWRR (only when in mwrr mode and not a debt ledger)
    const mwrrQuery = useLedgerHistoricalMwrr({
        ledgerId: mode === 'mwrr' && !isDebt ? ledger.id : 0,
        granularity,
    });

    // Determine which query to use based on mode
    const activeQuery = mode === 'mwrr' ? mwrrQuery : mode === 'pnl' ? pnlQuery : valueQuery;
    const { error, refetch } = activeQuery;

    // Loading states:
    // - initialPageLoad: First time loading, no data at all
    // - modeSwitchLoading: Switching modes, loading new data but have previous mode's data
    // - anyFetching: Any data fetching (for overlay)
    const isInitialPageLoad = valueQuery.isLoading && !valueQuery.data;
    const isModeSwitchLoading = (
        (mode === 'pnl' && pnlQuery.isLoading && !pnlQuery.data && !!valueQuery.data) ||
        (mode === 'mwrr' && mwrrQuery.isLoading && !mwrrQuery.data && !!valueQuery.data)
    );
    const showLoadingOverlay = activeQuery.isFetching;

    // Handle granularity change - reset brush and refetch
    const handleGranularityChange = useCallback((newGranularity: AnalyticsGranularity) => {
        setGranularity(newGranularity);
        setBrushRange(null); // Reset brush on granularity change
    }, [setGranularity]);

    // Handle brush change
    const handleBrushChange = useCallback((range: BrushRange | null) => {
        setBrushRange(range);
    }, []);

    // Transform data to match the chart's expected format
    // The chart expects { value: number | null, ... }
    const chartData = useMemo(() => {
        // For MWRR mode, transform mwrr data
        if (mode === 'mwrr' && mwrrQuery.data?.items) {
            return mwrrQuery.data.items.map((item) => ({
                periodEnd: item.periodEnd,
                periodLabel: item.periodLabel,
                // Use mwrr as the value for the chart
                value: item.mwrr,
                displayCurrency: mwrrQuery.data?.currency || 'USD',
                isCarriedForward: false,
                // Keep original field for tooltip
                mwrr: item.mwrr,
            }));
        }

        // For P&L mode, transform P&L data
        if (mode === 'pnl' && pnlQuery.data?.items) {
            return pnlQuery.data.items.map((item) => ({
                periodEnd: item.periodEnd,
                periodLabel: item.periodLabel,
                value: item.netProfit, // Use netProfit as the value for the chart
                displayCurrency: pnlQuery.data?.currency || 'USD',
                isCarriedForward: item.isCarriedForward,
                // Keep original P&L fields for tooltip
                netProfit: item.netProfit,
                gainPercentage: item.gainPercentage,
                changeFromPrevious: item.changeFromPrevious,
            }));
        }

        // Default: value mode
        return valueQuery.data?.items || [];
    }, [mode, pnlQuery.data, mwrrQuery.data, valueQuery.data]);

    // Build mode toggle options based on ledger type
    // For debt ledgers: only Value option
    // For asset ledgers: Value, P&L, and MWRR
    const modeOptions: ToggleOption[] = useMemo(() => {
        const options: ToggleOption[] = [
            { value: 'value', label: 'Value' },
            { value: 'pnl', label: 'P&L', hidden: isDebt },
            { value: 'mwrr', label: 'MWRR', hidden: isDebt },
        ];
        return options;
    }, [isDebt]);

    // Export configuration based on current mode
    const getExportColumns = (): ColumnDefinition[] => {
        if (mode === 'mwrr') return MWRR_EXPORT_COLUMNS;
        if (mode === 'pnl') return PNL_EXPORT_COLUMNS;
        return VALUE_EXPORT_COLUMNS;
    };
    const getExportData = (): unknown[] => {
        if (mode === 'mwrr') return mwrrQuery.data?.items || [];
        if (mode === 'pnl') return pnlQuery.data?.items || [];
        return valueQuery.data?.items || [];
    };
    const exportColumns = getExportColumns();
    const exportData = getExportData();
    const sanitizedLedgerName = sanitizeFilename(ledger.label);
    const exportFilename = `${sanitizedLedgerName}-${mode}-${formatDateISO()}`;

    // Initial page loading state (no data at all yet)
    if (isInitialPageLoad) {
        return (
            <div className="space-y-4">
                {/* Stats Cards */}
                <StatsCards ledgerId={ledger.id} isAsset={isAsset} />

                {/* Chart container with header */}
                <div className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-secondary">
                    {/* Header with controls - mobile: 2 rows, desktop: single row */}
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            {/* Left group: Mode | Granularity */}
                            <div className="flex items-center justify-between sm:justify-start gap-3">
                                <ModeToggle
                                    value={mode}
                                    onChange={setMode}
                                    options={modeOptions}
                                />
                                <div className="sm:pl-3 sm:border-l sm:border-gray-200 sm:dark:border-gray-600">
                                    <GranularityToggle value={granularity} onChange={handleGranularityChange} />
                                </div>
                            </div>

                            {/* Right group: Chart/Table | Download */}
                            <div className="flex items-center justify-between sm:justify-end gap-3">
                                <ChartTableToggle
                                    value={viewMode}
                                    onChange={setViewMode}
                                />
                                <div className="sm:pl-3 sm:border-l sm:border-gray-200 sm:dark:border-gray-600">
                                    <DownloadMenu
                                        data={exportData}
                                        columns={exportColumns}
                                        filename={exportFilename}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Loading state */}
                    <div className="flex justify-center py-12">
                        <div className="flex items-center space-x-2">
                            <Spinner />
                            <span className="text-gray-500 dark:text-gray-400">
                                Loading analytics...
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="space-y-4">
                {/* Stats Cards */}
                <StatsCards ledgerId={ledger.id} isAsset={isAsset} />

                {/* Chart container with header */}
                <div className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-secondary">
                    {/* Header with controls - mobile: 2 rows, desktop: single row */}
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            {/* Left group: Mode | Granularity */}
                            <div className="flex items-center justify-between sm:justify-start gap-3">
                                <ModeToggle
                                    value={mode}
                                    onChange={setMode}
                                    options={modeOptions}
                                />
                                <div className="sm:pl-3 sm:border-l sm:border-gray-200 sm:dark:border-gray-600">
                                    <GranularityToggle value={granularity} onChange={handleGranularityChange} />
                                </div>
                            </div>

                            {/* Right group: Chart/Table | Download */}
                            <div className="flex items-center justify-between sm:justify-end gap-3">
                                <ChartTableToggle
                                    value={viewMode}
                                    onChange={setViewMode}
                                />
                                <div className="sm:pl-3 sm:border-l sm:border-gray-200 sm:dark:border-gray-600">
                                    <DownloadMenu
                                        data={exportData}
                                        columns={exportColumns}
                                        filename={exportFilename}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Error state */}
                    <div className="p-4">
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                                <p className="text-red-700 dark:text-red-300">
                                    Failed to load analytics data. Please try again.
                                </p>
                                <button
                                    onClick={() => refetch()}
                                    className="text-red-700 dark:text-red-300 hover:text-red-900 dark:hover:text-red-100 font-medium text-sm"
                                >
                                    Retry
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // P&L empty state (no returns/cash flow entries) - but not while loading
    if (mode === 'pnl' && !isModeSwitchLoading && (!pnlQuery.data?.items || pnlQuery.data.items.length === 0)) {
        return (
            <div className="space-y-4">
                {/* Stats Cards */}
                <StatsCards ledgerId={ledger.id} isAsset={isAsset} />

                {/* Chart container with header */}
                <div className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-secondary">
                    {/* Header with controls - mobile: 2 rows, desktop: single row */}
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            {/* Left group: Mode | Granularity */}
                            <div className="flex items-center justify-between sm:justify-start gap-3">
                                <ModeToggle
                                    value={mode}
                                    onChange={setMode}
                                    options={modeOptions}
                                />
                                <div className="sm:pl-3 sm:border-l sm:border-gray-200 sm:dark:border-gray-600">
                                    <GranularityToggle value={granularity} onChange={handleGranularityChange} />
                                </div>
                            </div>

                            {/* Right group: Chart/Table | Download */}
                            <div className="flex items-center justify-between sm:justify-end gap-3">
                                <ChartTableToggle
                                    value={viewMode}
                                    onChange={setViewMode}
                                    disabled
                                />
                                <div className="sm:pl-3 sm:border-l sm:border-gray-200 sm:dark:border-gray-600">
                                    <DownloadMenu
                                        data={exportData}
                                        columns={exportColumns}
                                        filename={exportFilename}
                                        disabled
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* P&L Empty state */}
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                            Not enough data for P&L
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                            P&L requires cash flow history to calculate profit and loss.
                        </p>
                        <p className="text-sm mb-4">
                            <KnowledgeLink page="pnl">What is P&L?</KnowledgeLink>
                        </p>
                        <button
                            onClick={() => navigate(`/ledgers/${ledger.id}/cash-flow`)}
                            className="text-accent hover:text-accent/80 transition-colors text-sm"
                        >
                            Add cash flows →
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // MWRR empty state (no returns data or insufficient data) - but not while loading
    if (mode === 'mwrr' && !isModeSwitchLoading && (!mwrrQuery.data?.items || mwrrQuery.data.items.length === 0)) {
        return (
            <div className="space-y-4">
                {/* Stats Cards */}
                <StatsCards ledgerId={ledger.id} isAsset={isAsset} />

                {/* Chart container with header */}
                <div className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-secondary">
                    {/* Header with controls - mobile: 2 rows, desktop: single row */}
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            {/* Left group: Mode | Granularity */}
                            <div className="flex items-center justify-between sm:justify-start gap-3">
                                <ModeToggle
                                    value={mode}
                                    onChange={setMode}
                                    options={modeOptions}
                                />
                                <div className="sm:pl-3 sm:border-l sm:border-gray-200 sm:dark:border-gray-600">
                                    <GranularityToggle value={granularity} onChange={handleGranularityChange} />
                                </div>
                            </div>

                            {/* Right group: Chart/Table | Download */}
                            <div className="flex items-center justify-between sm:justify-end gap-3">
                                <ChartTableToggle
                                    value={viewMode}
                                    onChange={setViewMode}
                                    disabled
                                />
                                <div className="sm:pl-3 sm:border-l sm:border-gray-200 sm:dark:border-gray-600">
                                    <DownloadMenu
                                        data={exportData}
                                        columns={exportColumns}
                                        filename={exportFilename}
                                        disabled
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* MWRR Empty state */}
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                            Not enough data for MWRR
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                            MWRR requires at least 3 months of cash flow history to calculate returns.
                        </p>
                        <p className="text-sm mb-4">
                            <KnowledgeLink page="mwrr">What is MWRR?</KnowledgeLink>
                        </p>
                        <button
                            onClick={() => navigate(`/ledgers/${ledger.id}/cash-flow`)}
                            className="text-accent hover:text-accent/80 transition-colors text-sm"
                        >
                            Add cash flows →
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Value mode empty state (no historical values)
    if (mode === 'value' && (!valueQuery.data?.items || valueQuery.data.items.length === 0)) {
        return (
            <div className="space-y-4">
                {/* Stats Cards */}
                <StatsCards ledgerId={ledger.id} isAsset={isAsset} />

                {/* Chart container with header */}
                <div className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-secondary">
                    {/* Header with controls - mobile: 2 rows, desktop: single row */}
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            {/* Left group: Mode | Granularity */}
                            <div className="flex items-center justify-between sm:justify-start gap-3">
                                <ModeToggle
                                    value={mode}
                                    onChange={setMode}
                                    options={modeOptions}
                                />
                                <div className="sm:pl-3 sm:border-l sm:border-gray-200 sm:dark:border-gray-600">
                                    <GranularityToggle value={granularity} onChange={handleGranularityChange} />
                                </div>
                            </div>

                            {/* Right group: Chart/Table | Download */}
                            <div className="flex items-center justify-between sm:justify-end gap-3">
                                <ChartTableToggle
                                    value={viewMode}
                                    onChange={setViewMode}
                                    disabled
                                />
                                <div className="sm:pl-3 sm:border-l sm:border-gray-200 sm:dark:border-gray-600">
                                    <DownloadMenu
                                        data={exportData}
                                        columns={exportColumns}
                                        filename={exportFilename}
                                        disabled
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Empty state */}
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                            No historical data available
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                            Add more values to this {typeLabel} to see historical analytics.
                        </p>
                        <button
                            onClick={() => navigate(`/ledgers/${ledger.id}/values`)}
                            className="text-accent hover:text-accent/80 transition-colors text-sm"
                        >
                            Add values →
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Insufficient data state (only 1 data point - need at least 2 for a trend)
    if (chartData.length === 1) {
        return (
            <div className="space-y-4">
                {/* Stats Cards */}
                <StatsCards ledgerId={ledger.id} isAsset={isAsset} />

                {/* Chart container with header */}
                <div className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-secondary">
                    {/* Header with controls - mobile: 2 rows, desktop: single row */}
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            {/* Left group: Mode | Granularity */}
                            <div className="flex items-center justify-between sm:justify-start gap-3">
                                <ModeToggle
                                    value={mode}
                                    onChange={setMode}
                                    options={modeOptions}
                                />
                                <div className="sm:pl-3 sm:border-l sm:border-gray-200 sm:dark:border-gray-600">
                                    <GranularityToggle value={granularity} onChange={handleGranularityChange} />
                                </div>
                            </div>

                            {/* Right group: Chart/Table | Download */}
                            <div className="flex items-center justify-between sm:justify-end gap-3">
                                <ChartTableToggle
                                    value={viewMode}
                                    onChange={setViewMode}
                                    disabled
                                />
                                <div className="sm:pl-3 sm:border-l sm:border-gray-200 sm:dark:border-gray-600">
                                    <DownloadMenu
                                        data={exportData}
                                        columns={exportColumns}
                                        filename={exportFilename}
                                        disabled
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Insufficient data state */}
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                            Not enough data for this view
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {granularity === 'year'
                                ? 'Try switching to monthly view for more detail.'
                                : 'Add another month\'s value to see trends. Monthly updates are ideal.'}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Determine currency based on mode
    const currency = mode === 'mwrr'
        ? (mwrrQuery.data?.currency || 'USD')
        : mode === 'pnl'
            ? (pnlQuery.data?.currency || 'USD')
            : (valueQuery.data?.currency || 'USD');

    // Determine the custom tooltip for the chart
    const getCustomTooltip = () => {
        if (mode === 'pnl' && !isModeSwitchLoading) {
            return <PnlChartTooltip currency={currency} />;
        }
        if (isReturnsMode && !isModeSwitchLoading) {
            return <ReturnsChartTooltip />;
        }
        return undefined;
    };

    return (
        <div className="space-y-4">
            {/* Stats Cards */}
            <StatsCards ledgerId={ledger.id} isAsset={isAsset} />

            {/* Chart container with header - matching aggregated chart style */}
            <div
                ref={containerRef}
                className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-secondary"
            >
                {/* Header with controls - mobile: 2 rows, desktop: single row */}
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        {/* Left group: Mode | Granularity (separator on desktop only) */}
                        <div className="flex items-center justify-between sm:justify-start gap-3">
                            <ModeToggle
                                value={mode}
                                onChange={setMode}
                                options={modeOptions}
                            />
                            <div className="sm:pl-3 sm:border-l sm:border-gray-200 sm:dark:border-gray-600">
                                <GranularityToggle value={granularity} onChange={handleGranularityChange} />
                            </div>
                        </div>

                        {/* Right group: Chart/Table | Download (separator on desktop only) */}
                        <div className="flex items-center justify-between sm:justify-end gap-3">
                            <ChartTableToggle
                                value={viewMode}
                                onChange={setViewMode}
                            />
                            <div className="sm:pl-3 sm:border-l sm:border-gray-200 sm:dark:border-gray-600">
                                <DownloadMenu
                                    data={exportData}
                                    columns={exportColumns}
                                    filename={exportFilename}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content area - conditionally render chart or table */}
                {viewMode === 'table' ? (
                    <div className="relative">
                        {/* Loading overlay for table view */}
                        {(showLoadingOverlay || isModeSwitchLoading) && (
                            <div
                                data-testid="loading-overlay"
                                className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 flex items-center justify-center z-10"
                            >
                                <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-lg shadow-lg">
                                    <Spinner size="sm" />
                                    <span className="text-sm text-gray-600 dark:text-gray-300">
                                        Updating...
                                    </span>
                                </div>
                            </div>
                        )}
                        {mode === 'mwrr' ? (
                            <ReturnsTable
                                items={mwrrQuery.data?.items || []}
                            />
                        ) : mode === 'pnl' ? (
                            <ProfitLossTable
                                items={pnlQuery.data?.items || []}
                                currency={currency}
                            />
                        ) : (
                            <HistoricalValueTable
                                items={valueQuery.data?.items || []}
                                currency={currency}
                                isDebt={isDebt}
                            />
                        )}
                    </div>
                ) : (
                    <div className="relative px-4 pb-4">
                        {/* Loading overlay (during refetch or mode switch) */}
                        {(showLoadingOverlay || isModeSwitchLoading) && (
                            <div
                                data-testid="loading-overlay"
                                className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 flex items-center justify-center z-10"
                            >
                                <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-lg shadow-lg">
                                    <Spinner size="sm" />
                                    <span className="text-sm text-gray-600 dark:text-gray-300">
                                        Updating...
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Chart */}
                        <HistoricalValueChart
                            data={isModeSwitchLoading ? (valueQuery.data?.items || []) as HistoricalValueItem[] : chartData as HistoricalValueItem[]}
                            currency={currency}
                            onBrushChange={handleBrushChange}
                            isDebt={isDebt}
                            containerWidth={dimensions.width || 800}
                            brushRange={brushRange}
                            customTooltip={getCustomTooltip()}
                            isPercentMode={isReturnsMode && !isModeSwitchLoading}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
